import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Upload, 
  FileText, 
  Loader2, 
  AlertTriangle, 
  CheckCircle2, 
  Shield, 
  Clock, 
  XCircle,
  ChevronDown,
  ChevronRight,
  Zap,
  Target,
  FileSearch,
  RefreshCw,
  AlertCircle,
  X,
  TrendingDown,
  Award,
  Heart,
  ArrowLeft,
  Download,
  Database,
  BarChart3,
  TrendingUp,
  ExternalLink,
  Info,
  MessageSquareWarning
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PolicyXRayPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(selectedFile.type)) {
        setError("Please upload a PDF or image file (PNG, JPG)");
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }
      setFile(selectedFile);
      setError(null);
      setAnalysis(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(droppedFile.type)) {
        setError("Please upload a PDF or image file (PNG, JPG)");
        return;
      }
      if (droppedFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }
      setFile(droppedFile);
      setError(null);
      setAnalysis(null);
    }
  };

  const analyzePolicy = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    setError(null);
    setAnalysisStep("Extracting policy details...");
    
    const stepTimers = [
      setTimeout(() => setAnalysisStep("Searching for official policy documents..."), 8000),
      setTimeout(() => setAnalysisStep("Analyzing riders and coverage..."), 20000),
      setTimeout(() => setAnalysisStep("Calculating waiting periods..."), 35000),
      setTimeout(() => setAnalysisStep("Identifying gaps and recommendations..."), 50000),
      setTimeout(() => setAnalysisStep("Preparing your analysis report..."), 65000),
    ];
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const startRes = await axios.post(`${API}/policy-xray/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      const jobId = startRes.data.job_id;

      let job = null;
      for (let i = 0; i < 75; i++) {
        await new Promise(r => setTimeout(r, 4000));
        const poll = await axios.get(`${API}/policy-xray/status/${jobId}`, { timeout: 20000 });
        if (poll.data.status === 'done') { job = poll.data; break; }
        if (poll.data.status === 'failed') throw new Error(poll.data.error || 'Analysis failed');
      }
      if (!job) throw new Error('Analysis timed out. Please try again.');
      
      setAnalysis(job.result);
      setExpandedItems({ 
        policy_summary: true, 
        overall_score: true,
        whats_concerning: true 
      });
    } catch (err) {
      console.error("Analysis error:", err);
      setError(err.response?.data?.detail || err.message || "Failed to analyze policy. Please try again.");
    } finally {
      stepTimers.forEach(timer => clearTimeout(timer));
      setAnalyzing(false);
      setAnalysisStep("");
    }
  };

  const resetAnalysis = () => {
    setFile(null);
    setAnalysis(null);
    setError(null);
    setExpandedItems({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Generate PDF Report
  const generatePDF = async () => {
    if (!analysis) return;
    
    setDownloadingPdf(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      let y = 0;
      let pageNum = 1;
      
      // Color palette
      const colors = {
        primary: [88, 28, 135],      // Purple
        accent: [236, 72, 153],       // Pink
        success: [16, 185, 129],      // Green
        warning: [245, 158, 11],      // Amber
        danger: [239, 68, 68],        // Red
        info: [59, 130, 246],         // Blue
        cyan: [6, 182, 212],          // Cyan
        gray: [107, 114, 128],        // Gray
        darkGray: [55, 65, 81],       // Dark gray
        lightGray: [243, 244, 246],   // Light gray bg
        white: [255, 255, 255],
        black: [17, 24, 39]
      };
      
      // Helper: Check page break
      const checkPageBreak = (neededHeight) => {
        if (y + neededHeight > pageHeight - 25) {
          pdf.addPage();
          pageNum++;
          y = 20;
          return true;
        }
        return false;
      };
      
      // Helper: Add footer to current page
      const addFooter = () => {
        pdf.setFontSize(8);
        pdf.setTextColor(...colors.gray);
        pdf.text('Kavach Box X-Ray Report', margin, pageHeight - 10);
        pdf.text(`Page ${pageNum}`, pageWidth - margin - 15, pageHeight - 10);
      };
      
      // Helper: Draw rounded rectangle with optional border
      const drawBox = (x, yPos, w, h, fillColor, borderColor = null, radius = 3) => {
        pdf.setFillColor(...fillColor);
        pdf.roundedRect(x, yPos, w, h, radius, radius, 'F');
        if (borderColor) {
          pdf.setDrawColor(...borderColor);
          pdf.setLineWidth(0.3);
          pdf.roundedRect(x, yPos, w, h, radius, radius, 'S');
        }
      };
      
      // Helper: Add text with word wrap
      const addWrappedText = (text, x, yPos, maxWidth, fontSize, fontStyle = 'normal', color = colors.black) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text || '', maxWidth);
        let currentY = yPos;
        lines.forEach(line => {
          pdf.text(line, x, currentY);
          currentY += fontSize * 0.4;
        });
        return currentY - yPos;
      };
      
      // ==================== PAGE 1: HEADER ====================
      // Purple gradient header
      drawBox(0, 0, pageWidth, 45, colors.primary);
      drawBox(0, 35, pageWidth, 10, colors.accent);
      
      // Title
      pdf.setTextColor(...colors.white);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Policy X-Ray Report', margin, 22);
      
      // Subtitle
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const company = analysis.policy_summary?.company || analysis.policy_summary?.company_name || 'Insurance Policy';
      const product = analysis.policy_summary?.product || analysis.policy_summary?.product_name || '';
      pdf.text(`${company}${product ? ' - ' + product : ''}`, margin, 32);
      
      // Date
      pdf.setFontSize(9);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, pageWidth - margin - 45, 32);
      
      y = 55;
      
      // ==================== 1. POLICY SUMMARY (FIRST) ====================
      if (analysis.policy_summary) {
        const ps = analysis.policy_summary;
        
        // Section header
        drawBox(margin, y, contentWidth, 8, colors.info);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('POLICY SUMMARY', margin + 4, y + 5.5);
        y += 12;
        
        // Policy details in a grid
        const details = [
          ['Company', ps.company || ps.company_name || 'N/A'],
          ['Product', ps.product || ps.product_name || 'N/A'],
          ['Sum Insured', ps.sum_insured || 'N/A'],
          ['Premium', ps.premium || 'N/A'],
          ['Inception Date', ps.first_inception_date || 'N/A'],
          ['Coverage Duration', ps.months_since_inception ? `${ps.months_since_inception} months` : 'N/A']
        ];
        
        const colWidth = contentWidth / 2;
        details.forEach((item, i) => {
          const col = i % 2;
          const xPos = margin + (col * colWidth);
          
          if (col === 0 && i > 0) y += 7;
          
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.gray);
          pdf.text(item[0], xPos, y);
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...colors.darkGray);
          pdf.text(String(item[1]).substring(0, 40), xPos, y + 4);
        });
        y += 10;
        
        // Insured members
        if (ps.insured_members && ps.insured_members.length > 0) {
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.gray);
          pdf.text('Insured Members', margin, y);
          y += 4;
          
          pdf.setFontSize(9);
          pdf.setTextColor(...colors.darkGray);
          const members = ps.insured_members.map(m => `${m.name} (${m.relationship || m.relation}${m.age ? ', ' + m.age + 'y' : ''})`).join('  |  ');
          const memberLines = pdf.splitTextToSize(members, contentWidth);
          memberLines.forEach(line => {
            pdf.text(line, margin, y);
            y += 4;
          });
        }
        y += 8;
      }
      
      // ==================== 2. IRDAI CLAIM EXPERIENCE (SECOND - MOST IMPORTANT) ====================
      if (analysis.irdai_claim_experience && !analysis.irdai_claim_experience.error) {
        checkPageBreak(70);
        
        // Section header with "MOST IMPORTANT" badge
        drawBox(margin, y, contentWidth, 10, colors.warning);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('IRDAI CLAIM EXPERIENCE DATA', margin + 4, y + 7);
        
        // Most Important badge
        pdf.setFillColor(...colors.danger);
        pdf.roundedRect(pageWidth - margin - 35, y + 2, 32, 6, 2, 2, 'F');
        pdf.setFontSize(7);
        pdf.text('MOST IMPORTANT', pageWidth - margin - 33, y + 6);
        y += 14;
        
        // Data year and source
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...colors.gray);
        const irdai = analysis.irdai_claim_experience;
        pdf.text(`Data Year: ${irdai.data_year || 'FY 2023-24'}  |  Source: ${irdai.data_reliability || 'Official IRDAI'}`, margin, y);
        y += 6;
        
        // Three metrics in columns
        const metricWidth = (contentWidth - 10) / 3;
        
        // Metric 1: Claim Settlement Ratio
        if (irdai.claim_settlement_ratio) {
          drawBox(margin, y, metricWidth, 30, [236, 253, 245], colors.success);
          pdf.setTextColor(...colors.success);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text('CLAIM SETTLEMENT', margin + 3, y + 6);
          pdf.text('RATIO (CSR)', margin + 3, y + 10);
          
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text(irdai.claim_settlement_ratio.value || 'N/A', margin + 3, y + 20);
          
          if (irdai.claim_settlement_ratio.trend) {
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(...(irdai.claim_settlement_ratio.trend === 'Improved' ? colors.success : colors.danger));
            pdf.text(irdai.claim_settlement_ratio.trend, margin + 3, y + 26);
          }
        }
        
        // Metric 2: Incurred Claim Ratio
        if (irdai.incurred_claim_ratio) {
          drawBox(margin + metricWidth + 5, y, metricWidth, 30, [239, 246, 255], colors.info);
          pdf.setTextColor(...colors.info);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text('INCURRED CLAIM', margin + metricWidth + 8, y + 6);
          pdf.text('RATIO (ICR)', margin + metricWidth + 8, y + 10);
          
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text(irdai.incurred_claim_ratio.value || 'N/A', margin + metricWidth + 8, y + 20);
          
          if (irdai.incurred_claim_ratio.assessment) {
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            const assessColor = irdai.incurred_claim_ratio.assessment === 'Good' ? colors.success : 
                               irdai.incurred_claim_ratio.assessment === 'Concerning' ? colors.danger : colors.warning;
            pdf.setTextColor(...assessColor);
            pdf.text(irdai.incurred_claim_ratio.assessment, margin + metricWidth + 8, y + 26);
          }
        }
        
        // Metric 3: Complaints
        if (irdai.complaints_data) {
          drawBox(margin + 2 * (metricWidth + 5), y, metricWidth, 30, [250, 245, 255], colors.primary);
          pdf.setTextColor(...colors.primary);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text('COMPLAINTS', margin + 2 * (metricWidth + 5) + 3, y + 6);
          pdf.text('TO IRDAI', margin + 2 * (metricWidth + 5) + 3, y + 10);
          
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text(String(irdai.complaints_data.total_complaints || 'N/A'), margin + 2 * (metricWidth + 5) + 3, y + 20);
          
          if (irdai.complaints_data.complaints_per_10000_policies) {
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(...colors.gray);
            pdf.text(`${irdai.complaints_data.complaints_per_10000_policies} per 10k policies`, margin + 2 * (metricWidth + 5) + 3, y + 26);
          }
        }
        
        y += 35;
        
        // Explanations
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...colors.warning);
        pdf.text('What do these numbers mean?', margin, y);
        y += 5;
        
        // CSR explanation
        if (irdai.claim_settlement_ratio?.explanation) {
          checkPageBreak(15);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...colors.success);
          pdf.text('CSR:', margin, y);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.darkGray);
          const csrLines = pdf.splitTextToSize(irdai.claim_settlement_ratio.explanation, contentWidth - 12);
          csrLines.slice(0, 2).forEach(line => {
            pdf.text(line, margin + 12, y);
            y += 3.5;
          });
          y += 2;
        }
        
        // ICR explanation
        if (irdai.incurred_claim_ratio?.explanation) {
          checkPageBreak(15);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...colors.info);
          pdf.text('ICR:', margin, y);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.darkGray);
          const icrLines = pdf.splitTextToSize(irdai.incurred_claim_ratio.explanation, contentWidth - 12);
          icrLines.slice(0, 2).forEach(line => {
            pdf.text(line, margin + 12, y);
            y += 3.5;
          });
          y += 2;
        }
        
        // Complaints explanation
        if (irdai.complaints_data?.explanation) {
          checkPageBreak(15);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...colors.primary);
          pdf.text('Complaints:', margin, y);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.darkGray);
          const compLines = pdf.splitTextToSize(irdai.complaints_data.explanation, contentWidth - 22);
          compLines.slice(0, 2).forEach(line => {
            pdf.text(line, margin + 22, y);
            y += 3.5;
          });
          y += 2;
        }
        
        // Source link
        if (irdai.source_url) {
          y += 2;
          pdf.setFontSize(7);
          pdf.setTextColor(...colors.info);
          pdf.text(`Source: ${irdai.source_url}`, margin, y);
        }
        
        y += 8;
      }
      
      // ==================== 3. KEY STRENGTHS & GAPS (THIRD) ====================
      if (analysis.overall_score && (analysis.overall_score.key_strengths?.length > 0 || analysis.overall_score.key_gaps?.length > 0)) {
        checkPageBreak(50);
        
        const halfWidth = (contentWidth - 5) / 2;
        
        // Strengths column
        drawBox(margin, y, halfWidth, 8, colors.success);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('KEY STRENGTHS', margin + 4, y + 5.5);
        
        // Gaps column
        drawBox(margin + halfWidth + 5, y, halfWidth, 8, colors.danger);
        pdf.text('KEY GAPS', margin + halfWidth + 9, y + 5.5);
        
        y += 12;
        let leftY = y;
        let rightY = y;
        
        // Strengths items
        (analysis.overall_score.key_strengths || []).slice(0, 5).forEach(item => {
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.success);
          pdf.text('+', margin + 2, leftY);
          pdf.setTextColor(...colors.darkGray);
          const lines = pdf.splitTextToSize(item, halfWidth - 10);
          lines.forEach(line => {
            pdf.text(line, margin + 6, leftY);
            leftY += 3.5;
          });
          leftY += 2;
        });
        
        // Gaps items
        (analysis.overall_score.key_gaps || []).slice(0, 5).forEach(item => {
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.danger);
          pdf.text('-', margin + halfWidth + 7, rightY);
          pdf.setTextColor(...colors.darkGray);
          const lines = pdf.splitTextToSize(item, halfWidth - 10);
          lines.forEach(line => {
            pdf.text(line, margin + halfWidth + 11, rightY);
            rightY += 3.5;
          });
          rightY += 2;
        });
        
        y = Math.max(leftY, rightY) + 8;
      }
      
      // ==================== 4. OVERALL SCORE (FOURTH) ====================
      if (analysis.overall_score) {
        checkPageBreak(40);
        
        const scoreBoxHeight = 30;
        drawBox(margin, y, contentWidth, scoreBoxHeight, colors.lightGray, colors.primary);
        
        // Score circle
        const scoreX = margin + 25;
        const scoreY = y + scoreBoxHeight / 2;
        pdf.setFillColor(...colors.accent);
        pdf.circle(scoreX, scoreY, 10, 'F');
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        const scoreText = analysis.overall_score.score || 'N/A';
        pdf.text(scoreText, scoreX - (scoreText.length > 3 ? 5 : 3), scoreY + 4);
        
        // Score label and verdict
        pdf.setTextColor(...colors.darkGray);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Overall Policy Score', margin + 45, y + 12);
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...colors.gray);
        const verdictLines = pdf.splitTextToSize(analysis.overall_score.verdict || '', contentWidth - 55);
        verdictLines.slice(0, 2).forEach((line, i) => {
          pdf.text(line, margin + 45, y + 18 + (i * 3.5));
        });
        
        y += scoreBoxHeight + 8;
      }
      
      // ==================== WHAT'S CONCERNING ====================
      if (analysis.whats_concerning && analysis.whats_concerning.length > 0) {
        checkPageBreak(40);
        
        drawBox(margin, y, contentWidth, 8, colors.danger);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('WHAT\'S CONCERNING', margin + 4, y + 5.5);
        y += 12;
        
        analysis.whats_concerning.slice(0, 4).forEach((item, idx) => {
          checkPageBreak(25);
          
          // Item box
          drawBox(margin, y, contentWidth, 22, colors.lightGray, [254, 202, 202]);
          
          // Number badge
          pdf.setFillColor(...colors.danger);
          pdf.circle(margin + 6, y + 6, 4, 'F');
          pdf.setTextColor(...colors.white);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.text(String(idx + 1), margin + 4.5, y + 7.5);
          
          // Concern text
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          const concernText = (item.concern || '').substring(0, 70);
          pdf.text(concernText, margin + 14, y + 6);
          
          // Impact
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.gray);
          const impactText = `Impact: ${(item.impact || '').substring(0, 80)}`;
          pdf.text(impactText, margin + 14, y + 12);
          
          // Solution
          pdf.setTextColor(...colors.success);
          const solutionText = `Solution: ${(item.solution || '').substring(0, 80)}`;
          pdf.text(solutionText, margin + 14, y + 18);
          
          y += 26;
        });
        y += 4;
      }
      
      // ==================== WHAT'S GOOD ====================
      if (analysis.whats_good && analysis.whats_good.length > 0) {
        checkPageBreak(35);
        
        drawBox(margin, y, contentWidth, 8, colors.success);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('WHAT\'S GOOD', margin + 4, y + 5.5);
        y += 12;
        
        analysis.whats_good.slice(0, 5).forEach((item, idx) => {
          checkPageBreak(15);
          
          drawBox(margin, y, contentWidth, 12, colors.lightGray, [167, 243, 208]);
          
          pdf.setTextColor(...colors.success);
          pdf.setFontSize(10);
          pdf.text('+', margin + 4, y + 7);
          
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text((item.feature || '').substring(0, 50), margin + 10, y + 5);
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(...colors.gray);
          pdf.text((item.why_good || '').substring(0, 90), margin + 10, y + 10);
          
          y += 15;
        });
        y += 4;
      }
      
      // ==================== WAITING PERIODS ====================
      if (analysis.waiting_period_analysis && analysis.waiting_period_analysis.length > 0) {
        checkPageBreak(35);
        
        drawBox(margin, y, contentWidth, 8, colors.warning);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('WAITING PERIOD STATUS', margin + 4, y + 5.5);
        y += 12;
        
        // Table header
        drawBox(margin, y, contentWidth, 7, [229, 231, 235]);
        pdf.setTextColor(...colors.darkGray);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Type', margin + 3, y + 5);
        pdf.text('Period', margin + 70, y + 5);
        pdf.text('Status', margin + 105, y + 5);
        pdf.text('Remaining', margin + 140, y + 5);
        y += 9;
        
        analysis.waiting_period_analysis.slice(0, 5).forEach((wp, idx) => {
          checkPageBreak(8);
          
          if (idx % 2 === 0) {
            drawBox(margin, y - 2, contentWidth, 8, [249, 250, 251]);
          }
          
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.darkGray);
          pdf.text((wp.type || '').substring(0, 35), margin + 3, y + 3);
          pdf.text((wp.standard_period || '').substring(0, 15), margin + 70, y + 3);
          
          // Status badge
          const isCompleted = (wp.status || '').toUpperCase().includes('COMPLETED');
          pdf.setFillColor(...(isCompleted ? colors.success : colors.warning));
          pdf.roundedRect(margin + 105, y - 1, 25, 6, 2, 2, 'F');
          pdf.setTextColor(...colors.white);
          pdf.setFontSize(7);
          pdf.text(isCompleted ? 'DONE' : 'ACTIVE', margin + 110, y + 3);
          
          pdf.setTextColor(...colors.gray);
          pdf.setFontSize(8);
          pdf.text(wp.remaining_days ? `${wp.remaining_days} days` : 'N/A', margin + 140, y + 3);
          
          y += 8;
        });
        y += 6;
      }
      
      // ==================== RIDERS ANALYSIS ====================
      if (analysis.riders_analysis) {
        const ra = analysis.riders_analysis;
        
        // Riders You Have
        if (ra.riders_taken_by_customer && ra.riders_taken_by_customer.length > 0) {
          checkPageBreak(30);
          
          drawBox(margin, y, contentWidth, 8, colors.cyan);
          pdf.setTextColor(...colors.white);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('RIDERS YOU HAVE', margin + 4, y + 5.5);
          y += 12;
          
          ra.riders_taken_by_customer.slice(0, 5).forEach(r => {
            checkPageBreak(12);
            
            drawBox(margin, y, contentWidth, 10, colors.lightGray);
            
            pdf.setTextColor(...colors.cyan);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.text((r.rider_name || '').substring(0, 40), margin + 4, y + 5);
            
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(...colors.gray);
            pdf.setFontSize(8);
            pdf.text((r.benefit || '').substring(0, 70), margin + 4, y + 9);
            
            y += 12;
          });
          y += 4;
        }
        
        // Missing Riders
        if (ra.riders_missing_but_recommended && ra.riders_missing_but_recommended.length > 0) {
          checkPageBreak(30);
          
          drawBox(margin, y, contentWidth, 8, colors.danger);
          pdf.setTextColor(...colors.white);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('RECOMMENDED RIDERS (MISSING)', margin + 4, y + 5.5);
          y += 12;
          
          ra.riders_missing_but_recommended.slice(0, 4).forEach(r => {
            checkPageBreak(15);
            
            drawBox(margin, y, contentWidth, 13, [254, 242, 242], [254, 202, 202]);
            
            pdf.setTextColor(...colors.danger);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.text((r.rider_name || '').substring(0, 40), margin + 4, y + 5);
            
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(...colors.darkGray);
            pdf.setFontSize(8);
            pdf.text(`Why: ${(r.why_recommended || '').substring(0, 80)}`, margin + 4, y + 10);
            
            y += 15;
          });
          y += 4;
        }
      }
      
      // ==================== RIDER COMBINATION VERDICT ====================
      if (analysis.rider_combination_verdict && analysis.rider_combination_verdict.verdict) {
        const rc = analysis.rider_combination_verdict;
        checkPageBreak(30);
        drawBox(margin, y, contentWidth, 8, colors.cyan);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`RIDER COMBINATION: ${(rc.verdict || '').replace(/_/g, ' ')}`, margin + 4, y + 5.5);
        y += 12;
        if (rc.one_liner) {
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          const lines = pdf.splitTextToSize(rc.one_liner, contentWidth - 8);
          checkPageBreak(lines.length * 5 + 4);
          pdf.text(lines, margin + 4, y + 2);
          y += lines.length * 5 + 3;
        }
        if (rc.summary) {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.gray);
          pdf.setFontSize(8);
          const lines = pdf.splitTextToSize(rc.summary, contentWidth - 8);
          checkPageBreak(lines.length * 4 + 4);
          pdf.text(lines, margin + 4, y + 2);
          y += lines.length * 4 + 4;
        }
        if (rc.gaps && rc.gaps.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(8);
          rc.gaps.slice(0, 4).forEach(g => {
            const lines = pdf.splitTextToSize(`- ${g}`, contentWidth - 8);
            checkPageBreak(lines.length * 4 + 2);
            pdf.text(lines, margin + 4, y + 2);
            y += lines.length * 4 + 2;
          });
        }
        y += 4;
      }
      
      // ==================== ACTION ITEMS ====================
      if (analysis.action_items && analysis.action_items.length > 0) {
        checkPageBreak(35);
        
        drawBox(margin, y, contentWidth, 8, colors.success);
        pdf.setTextColor(...colors.white);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('RECOMMENDED ACTIONS', margin + 4, y + 5.5);
        y += 12;
        
        analysis.action_items.slice(0, 5).forEach((item, idx) => {
          checkPageBreak(18);
          
          drawBox(margin, y, contentWidth, 15, colors.lightGray);
          
          // Priority badge
          const priorityColor = (item.priority || '').toUpperCase() === 'HIGH' ? colors.danger : 
                               (item.priority || '').toUpperCase() === 'MEDIUM' ? colors.warning : colors.info;
          pdf.setFillColor(...priorityColor);
          pdf.roundedRect(margin + 3, y + 2, 18, 5, 1.5, 1.5, 'F');
          pdf.setTextColor(...colors.white);
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.text((item.priority || 'LOW').toUpperCase(), margin + 5, y + 5.5);
          
          // Action text
          pdf.setTextColor(...colors.darkGray);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.text((item.action || '').substring(0, 60), margin + 25, y + 6);
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(...colors.gray);
          pdf.text(`${(item.reason || '').substring(0, 80)}`, margin + 25, y + 11);
          
          y += 18;
        });
      }
      
      // ==================== FOOTER ====================
      addFooter();
      
      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(...colors.gray);
        pdf.text('Kavach Box X-Ray Report  |  Always verify with your insurer for final decisions', margin, pageHeight - 10);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 10);
      }
      
      // Save PDF
      const fileName = `${company.replace(/[^a-zA-Z0-9]/g, '_')}_${product.replace(/[^a-zA-Z0-9]/g, '_')}_XRay_Report.pdf`;
      pdf.save(fileName);
      
    } catch (err) {
      console.error('PDF generation error:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const toggleItem = (key, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Clickable expandable item component - NO ANIMATION to prevent scroll issues
  const ClickableItem = ({ id, title, subtitle, icon: Icon, color, badge, badgeColor, children }) => {
    const isExpanded = expandedItems[id];
    
    return (
      <div className="border border-[#C9D4E6] rounded-xl overflow-hidden mb-3">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
          }}
          className={`w-full p-4 flex items-center gap-4 bg-gradient-to-r ${color} hover:opacity-90 transition-all cursor-pointer select-none text-left`}
        >
          <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-[#1B2A4A]" />
          </div>
          <div className="flex-1 text-left">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#1B2A4A]">{title}</span>
              {badge && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor || 'bg-white/20 text-[#1B2A4A]'}`}>
                  {badge}
                </span>
              )}
            </div>
            {subtitle && <p className="text-[#1B2A4A]/70 text-sm">{subtitle}</p>}
          </div>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-[#1B2A4A] flex-shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-[#1B2A4A] flex-shrink-0" />
          )}
        </button>
        
        {isExpanded && (
          <div className="p-4 bg-[#EDF1F8]/70 border-t border-[#C9D4E6]">
            {children}
          </div>
        )}
      </div>
    );
  };

  // Sub-item that's also clickable - NO ANIMATION
  const SubItem = ({ id, title, status, statusColor, children }) => {
    const isExpanded = expandedItems[id];
    
    return (
      <div className="border border-[#C9D4E6] rounded-lg overflow-hidden mb-2">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
          }}
          className="w-full p-3 flex items-center justify-between bg-[#EDF1F8]/60 hover:bg-[#EDF1F8] transition-colors cursor-pointer select-none text-left"
        >
          <span className="text-[#1B2A4A] text-sm font-medium text-left">{title}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {status && (
              <span className={`px-2 py-0.5 rounded text-xs ${statusColor || 'bg-[#EDF1F8] text-[#2E3B57]'}`}>
                {status}
              </span>
            )}
            {isExpanded ? <ChevronDown className="h-4 w-4 text-[#3A4557]" /> : <ChevronRight className="h-4 w-4 text-[#3A4557]" />}
          </div>
        </button>
        
        {isExpanded && (
          <div className="p-3 bg-[#EDF1F8]/70 text-sm text-[#2E3B57]">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#C9D4E6]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#3A4557] hover:text-[#1B2A4A] transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Home</span>
          </button>
          <h1 className="text-xl font-bold text-[#1B2A4A]">
            Policy <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">X-Ray</span>
          </h1>
          <div className="w-32"></div>
        </div>
      </header>

      <main className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-6">
              <FileSearch className="h-4 w-4 text-purple-600" />
              <span className="text-purple-700 text-sm font-medium">AI-Powered Analysis</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1B2A4A] mb-4">
              Analyze Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Policy</span>
            </h2>
            
            <p className="text-[#3A4557] text-lg max-w-2xl mx-auto">
              Upload your policy document. We'll fetch official policy wordings from the insurer and analyze what's <span className="text-emerald-600">good</span>, what's <span className="text-red-600">missing</span>, and what you <span className="text-[#A87608]">need to know</span>.
            </p>
            
            {/* Beta Disclaimer */}
            <div className="mt-6 p-4 rounded-xl bg-[#D4A017]/10 border border-[#D4A017]/30 max-w-2xl mx-auto">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-[#D4A017]/15 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-[#A87608]" />
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[#A87608] font-semibold text-sm">Beta Version</span>
                    <span className="px-2 py-0.5 bg-[#D4A017]/15 text-[#A87608] text-xs rounded-full">Experimental</span>
                  </div>
                  <p className="text-[#8F6A06]/80 text-sm leading-relaxed">
                    This AI-powered analysis is in <strong>beta</strong> and may not be 100% accurate. Results are based on AI interpretation and should be used for <strong>reference only</strong>. 
                    Always verify important details with your insurance company or advisor before making decisions.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Upload Area */}
          {!analysis && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-8 border border-purple-500/30"
            >
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                  file ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-purple-500/30 hover:border-purple-500/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {file ? (
                  <div className="space-y-4">
                    <div className="h-16 w-16 mx-auto rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                      <FileText className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[#1B2A4A] font-semibold text-lg">{file.name}</p>
                      <p className="text-[#3A4557] text-sm">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <Button
                      onClick={(e) => { e.stopPropagation(); resetAnalysis(); }}
                      variant="outline"
                      size="sm"
                      className="border-[#C9D4E6] text-[#3A4557]"
                    >
                      <X className="h-4 w-4 mr-2" />Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="h-16 w-16 mx-auto rounded-2xl bg-purple-500/20 flex items-center justify-center">
                      <Upload className="h-8 w-8 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-[#1B2A4A] font-semibold text-lg">Drop your policy document here</p>
                      <p className="text-[#3A4557] text-sm">or click to browse</p>
                    </div>
                    <p className="text-[#3A4557] text-xs">PDF, PNG, JPG (Max 10MB)</p>
                  </div>
                )}
              </div>

              {error && (
                <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {file && !analyzing && (
                <div className="mt-6 text-center">
                  <Button onClick={analyzePolicy} className="btn-primary px-8 py-6 text-lg" data-testid="analyze-btn">
                    <FileSearch className="h-5 w-5 mr-2" />
                    Analyze My Policy
                  </Button>
                </div>
              )}

              {analyzing && (
                <div className="mt-6 text-center space-y-4">
                  <div className="flex items-center justify-center gap-3">
                    <Loader2 className="h-6 w-6 text-purple-600 animate-spin" />
                    <span className="text-[#1B2A4A] font-medium">{analysisStep}</span>
                  </div>
                  <p className="text-[#3A4557] text-xs">This may take 1-2 minutes</p>
                  <div className="w-64 mx-auto h-2 bg-[#E2E9F4] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 90, ease: "linear" }}
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Analysis Results */}
          {analysis && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              
              {/* Action Buttons */}
              <div className="flex justify-between items-center gap-4 flex-wrap">
                {/* Learning Intelligence Badge */}
                {analysis.learning_stats ? (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                    analysis.learning_stats.analyses_of_this_product > 1
                      ? 'bg-emerald-500/20 text-emerald-600 border border-emerald-500/30'
                      : 'bg-[#EDF1F8]/40 text-[#2E3B57] border border-blue-500/30'
                  }`}>
                    <Database className="h-3 w-3" />
                    {analysis.learning_stats.analyses_of_this_product > 1
                      ? `Powered by ${analysis.learning_stats.analyses_of_this_product} analyses`
                      : 'First analysis of this product'}
                  </div>
                ) : analysis.knowledge_source && (
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                    analysis.knowledge_source === 'cache' 
                      ? 'bg-emerald-500/20 text-emerald-600 border border-emerald-500/30'
                      : 'bg-[#EDF1F8]/40 text-[#2E3B57] border border-blue-500/30'
                  }`}>
                    <Database className="h-3 w-3" />
                    {analysis.knowledge_source === 'cache' ? 'From Memory' : 'Fresh Analysis'}
                  </div>
                )}
                
                <div className="flex gap-3 ml-auto">
                  <Button 
                    onClick={generatePDF} 
                    disabled={downloadingPdf}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    data-testid="download-pdf-btn"
                  >
                    {downloadingPdf ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating PDF...</>
                    ) : (
                      <><Download className="h-4 w-4 mr-2" />Download Report</>
                    )}
                  </Button>
                  <Button onClick={resetAnalysis} variant="outline" className="border-purple-500/30 text-purple-600">
                    <RefreshCw className="h-4 w-4 mr-2" />Analyze Another
                  </Button>
                </div>
              </div>

              {/* RENEWAL VERDICT - The Decision */}
              {analysis.renewal_verdict && analysis.renewal_verdict.decision && analysis.renewal_verdict.decision !== 'REVIEW' && (
                <div className={`rounded-2xl border-2 p-6 ${
                  analysis.renewal_verdict.decision === 'RENEW' ? 'bg-emerald-500/10 border-emerald-500/40' :
                  analysis.renewal_verdict.decision === 'CONSIDER_PORTING' ? 'bg-rose-500/10 border-rose-500/40' :
                  'bg-amber-500/10 border-amber-500/40'
                }`} data-testid="renewal-verdict-banner">
                  <p className="text-[#3A4557] text-xs font-bold tracking-widest uppercase mb-1">Renewal Decision</p>
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h2 className={`text-2xl font-bold ${
                      analysis.renewal_verdict.decision === 'RENEW' ? 'text-emerald-600' :
                      analysis.renewal_verdict.decision === 'CONSIDER_PORTING' ? 'text-rose-600' : 'text-amber-600'
                    }`}>
                      {analysis.renewal_verdict.decision === 'RENEW' ? '✓ RENEW THIS POLICY' :
                       analysis.renewal_verdict.decision === 'CONSIDER_PORTING' ? '⇄ CONSIDER PORTING' :
                       '⚠ RENEW — WITH CHANGES'}
                    </h2>
                    {analysis.renewal_verdict.confidence && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#EDF1F8] text-[#1B2A4A]">
                        {analysis.renewal_verdict.confidence} CONFIDENCE
                      </span>
                    )}
                  </div>
                  {analysis.renewal_verdict.reasoning && (
                    <p className="text-[#2E3B57] text-sm leading-relaxed mb-3">{analysis.renewal_verdict.reasoning}</p>
                  )}
                  {analysis.renewal_verdict.changes_before_renewal?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[#1B2A4A] font-bold text-sm mb-1.5">Changes to make at this renewal:</p>
                      <ul className="space-y-1">
                        {analysis.renewal_verdict.changes_before_renewal.map((c, i) => (
                          <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analysis.renewal_verdict.porting_pointers?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[#1B2A4A] font-bold text-sm mb-1.5">If you explore porting:</p>
                      <ul className="space-y-1">
                        {analysis.renewal_verdict.porting_pointers.map((c, i) => (
                          <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                            <RefreshCw className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analysis.renewal_verdict.bottom_line && (
                    <p className="text-[#1B2A4A] font-medium text-sm border-t border-[#C9D4E6] pt-3">
                      Bottom line: {analysis.renewal_verdict.bottom_line}
                    </p>
                  )}
                </div>
              )}

              {/* 1. POLICY SUMMARY - First */}
              {analysis.policy_summary && (
                <ClickableItem
                  id="policy_summary"
                  title="Policy Summary"
                  subtitle={`${analysis.policy_summary.company || ''} - ${analysis.policy_summary.product || ''}`}
                  icon={FileText}
                  color="from-blue-600/30 to-cyan-600/30"
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(analysis.policy_summary).map(([key, value]) => {
                      if (key === 'insured_members') return null;
                      if (!value || value === 'Not specified') return null;
                      return (
                        <div key={key} className="bg-[#EDF1F8]/60 rounded-lg p-3">
                          <p className="text-[#3A4557] text-xs uppercase">{key.replace(/_/g, ' ')}</p>
                          <p className="text-[#1B2A4A] text-sm font-medium">{Array.isArray(value) ? value.join(', ') : String(value)}</p>
                        </div>
                      );
                    })}
                  </div>
                  {analysis.policy_summary.insured_members && analysis.policy_summary.insured_members.length > 0 && (
                    <div className="mt-3 p-3 bg-[#EDF1F8]/60 rounded-lg">
                      <p className="text-[#3A4557] text-xs uppercase mb-2">Insured Members</p>
                      <div className="flex flex-wrap gap-2">
                        {analysis.policy_summary.insured_members.map((m, i) => (
                          <span key={i} className="px-3 py-1 bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-sm">
                            {m.name} ({m.relationship}, {m.age}y)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </ClickableItem>
              )}

              {/* 2. IRDAI CLAIM EXPERIENCE - Second (MOST IMPORTANT) */}
              {analysis.irdai_claim_experience && !analysis.irdai_claim_experience.error && (
                <ClickableItem
                  id="irdai_claim_experience"
                  title="IRDAI Claim Experience Data"
                  subtitle={`Official regulatory data for ${analysis.irdai_claim_experience.company || 'this insurer'}`}
                  icon={BarChart3}
                  color="from-[#D4A017]/40 to-[#D9A82C]/40"
                  badge="LEVEL 1"
                  badgeColor="bg-[#D4A017] text-[#1B2A4A]"
                >
                  <div className="space-y-4">
                    {/* Data Year Badge */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 bg-[#D4A017]/15 text-[#A87608] rounded-full text-sm font-medium">
                          {analysis.irdai_claim_experience.data_year || 'FY 2023-24'}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          analysis.irdai_claim_experience.data_reliability === 'Official IRDAI' 
                            ? 'bg-emerald-500/20 text-emerald-600' 
                            : 'bg-[#D4A017]/15 text-[#A87608]'
                        }`}>
                          {analysis.irdai_claim_experience.data_reliability || 'Official IRDAI'}
                        </span>
                      </div>
                      {analysis.irdai_claim_experience.source_url && (
                        <a 
                          href={analysis.irdai_claim_experience.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#2E3B57] hover:text-[#2E3B57] text-sm transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View IRDAI Source
                        </a>
                      )}
                    </div>

                    {/* Three Key Metrics */}
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Claim Settlement Ratio */}
                      {analysis.irdai_claim_experience.claim_settlement_ratio && (
                        <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            <span className="text-emerald-600 font-semibold text-sm">Claim Settlement Ratio</span>
                          </div>
                          <div className="text-3xl font-bold text-[#1B2A4A] mb-1">
                            {analysis.irdai_claim_experience.claim_settlement_ratio.value || 'N/A'}
                          </div>
                          {analysis.irdai_claim_experience.claim_settlement_ratio.trend && (
                            <div className={`flex items-center gap-1 text-sm ${
                              analysis.irdai_claim_experience.claim_settlement_ratio.trend === 'Improved' ? 'text-emerald-600' :
                              analysis.irdai_claim_experience.claim_settlement_ratio.trend === 'Declined' ? 'text-red-600' : 'text-[#3A4557]'
                            }`}>
                              <TrendingUp className="h-4 w-4" />
                              {analysis.irdai_claim_experience.claim_settlement_ratio.trend}
                              {analysis.irdai_claim_experience.claim_settlement_ratio.previous_year && (
                                <span className="text-[#3A4557] ml-1">
                                  (was {analysis.irdai_claim_experience.claim_settlement_ratio.previous_year})
                                </span>
                              )}
                            </div>
                          )}
                          {analysis.irdai_claim_experience.claim_settlement_ratio.ranking && (
                            <p className="text-[#3A4557] text-xs mt-1">{analysis.irdai_claim_experience.claim_settlement_ratio.ranking}</p>
                          )}
                        </div>
                      )}

                      {/* Incurred Claim Ratio */}
                      {analysis.irdai_claim_experience.incurred_claim_ratio && (
                        <div className="bg-gradient-to-br from-[#EDF1F8]/10 to-[#DCE4F2]/5 border border-blue-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <BarChart3 className="h-5 w-5 text-[#2E3B57]" />
                            <span className="text-[#2E3B57] font-semibold text-sm">Incurred Claim Ratio</span>
                          </div>
                          <div className="text-3xl font-bold text-[#1B2A4A] mb-1">
                            {analysis.irdai_claim_experience.incurred_claim_ratio.value || 'N/A'}
                          </div>
                          {analysis.irdai_claim_experience.incurred_claim_ratio.assessment && (
                            <div className={`text-sm ${
                              analysis.irdai_claim_experience.incurred_claim_ratio.assessment === 'Good' ? 'text-emerald-600' :
                              analysis.irdai_claim_experience.incurred_claim_ratio.assessment === 'Concerning' ? 'text-red-600' : 'text-[#A87608]'
                            }`}>
                              Assessment: {analysis.irdai_claim_experience.incurred_claim_ratio.assessment}
                            </div>
                          )}
                          {analysis.irdai_claim_experience.incurred_claim_ratio.previous_year && (
                            <p className="text-[#3A4557] text-xs mt-1">
                              Previous year: {analysis.irdai_claim_experience.incurred_claim_ratio.previous_year}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Complaints Data */}
                      {analysis.irdai_claim_experience.complaints_data && (
                        <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquareWarning className="h-5 w-5 text-purple-600" />
                            <span className="text-purple-600 font-semibold text-sm">Complaints to IRDAI</span>
                          </div>
                          <div className="text-3xl font-bold text-[#1B2A4A] mb-1">
                            {analysis.irdai_claim_experience.complaints_data.total_complaints || 'N/A'}
                          </div>
                          {analysis.irdai_claim_experience.complaints_data.complaints_per_10000_policies && (
                            <p className="text-[#3A4557] text-sm">
                              {analysis.irdai_claim_experience.complaints_data.complaints_per_10000_policies} per 10,000 policies
                            </p>
                          )}
                          {analysis.irdai_claim_experience.complaints_data.resolution_rate && (
                            <p className="text-emerald-600 text-xs mt-1">
                              {analysis.irdai_claim_experience.complaints_data.resolution_rate} resolved
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 3-Year Track Record Table */}
                    {analysis.irdai_claim_experience.three_year_table?.length > 0 && (
                      <div data-testid="xray-three-year-table">
                        <p className="text-[#A87608] font-medium text-sm mb-2">3-Year Track Record</p>
                        <div className="rounded-xl overflow-hidden border border-[#C9D4E6]">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#EDF1F8]/60">
                                <th className="text-left p-2.5 text-[#3A4557] text-xs font-bold">Year</th>
                                <th className="text-left p-2.5 text-[#3A4557] text-xs font-bold">Claims settled in 3 months</th>
                                <th className="text-left p-2.5 text-[#3A4557] text-xs font-bold">Incurred Claim Ratio</th>
                                <th className="text-left p-2.5 text-[#3A4557] text-xs font-bold">Complaints</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analysis.irdai_claim_experience.three_year_table.map((y, i) => (
                                <tr key={i} className="border-t border-[#C9D4E6]">
                                  <td className="p-2.5 text-[#2E3B57] text-xs">{y.year}</td>
                                  <td className="p-2.5 text-emerald-600 font-bold">{y.csr}%</td>
                                  <td className="p-2.5 text-[#1B2A4A]">{y.icr}%</td>
                                  <td className="p-2.5 text-[#2E3B57]">{Number(y.complaints).toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {analysis.irdai_claim_experience.industry_average && (
                          <p className="text-[#3A4557] text-xs mt-2">
                            Industry average: CSR ~{analysis.irdai_claim_experience.industry_average.csr}% · ICR ~{analysis.irdai_claim_experience.industry_average.icr}%
                          </p>
                        )}
                      </div>
                    )}

                    {/* Advisor read */}
                    {analysis.irdai_claim_experience.advisor_read && (
                      <div className="p-3 rounded-lg bg-[#D4A017]/10 border border-[#D4A017]/30">
                        <p className="text-[#2E3B57] text-sm leading-relaxed"><span className="text-[#A87608] font-bold">Advisor's read: </span>{analysis.irdai_claim_experience.advisor_read}</p>
                      </div>
                    )}

                    {/* Better performers */}
                    {analysis.irdai_claim_experience.better_performers?.length > 0 && (
                      <div data-testid="xray-better-performers">
                        <p className="text-[#1B2A4A] font-medium text-sm mb-2">Insurers with a stronger claim track record (for factual comparison):</p>
                        <div className="flex flex-wrap gap-2">
                          {analysis.irdai_claim_experience.better_performers.map((b, i) => (
                            <span key={i} className="px-3 py-1.5 rounded-full text-xs bg-[#EDF1F8]/60 border border-[#C9D4E6] text-[#2E3B57]">
                              <span className="text-[#1B2A4A] font-bold">{b.name}</span> — {b.note}
                            </span>
                          ))}
                        </div>
                        <p className="text-[#3A4557] text-[11px] mt-1.5 italic">Company-level claim data only — not a product recommendation.</p>
                      </div>
                    )}

                    {/* Explanations */}
                    <div className="space-y-3 pt-2">
                      <p className="text-[#A87608] font-medium flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        What do these numbers mean?
                      </p>
                      
                      {/* CSR Explanation */}
                      {analysis.irdai_claim_experience.claim_settlement_ratio?.explanation && (
                        <div className="bg-[#EDF1F8]/60 rounded-lg p-3">
                          <p className="text-emerald-600 font-medium text-sm mb-1">Claim Settlement Ratio (CSR)</p>
                          <p className="text-[#2E3B57] text-sm">{analysis.irdai_claim_experience.claim_settlement_ratio.explanation}</p>
                          <p className="text-[#A87608] text-xs mt-2 flex items-start gap-1">
                            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            {analysis.irdai_claim_experience.claim_settlement_ratio.importance}
                          </p>
                        </div>
                      )}

                      {/* ICR Explanation */}
                      {analysis.irdai_claim_experience.incurred_claim_ratio?.explanation && (
                        <div className="bg-[#EDF1F8]/60 rounded-lg p-3">
                          <p className="text-[#2E3B57] font-medium text-sm mb-1">Incurred Claim Ratio (ICR)</p>
                          <p className="text-[#2E3B57] text-sm">{analysis.irdai_claim_experience.incurred_claim_ratio.explanation}</p>
                          <p className="text-[#A87608] text-xs mt-2 flex items-start gap-1">
                            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            {analysis.irdai_claim_experience.incurred_claim_ratio.importance}
                          </p>
                        </div>
                      )}

                      {/* Complaints Explanation */}
                      {analysis.irdai_claim_experience.complaints_data?.explanation && (
                        <div className="bg-[#EDF1F8]/60 rounded-lg p-3">
                          <p className="text-purple-600 font-medium text-sm mb-1">Complaints Data</p>
                          <p className="text-[#2E3B57] text-sm">{analysis.irdai_claim_experience.complaints_data.explanation}</p>
                          <p className="text-[#A87608] text-xs mt-2 flex items-start gap-1">
                            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            {analysis.irdai_claim_experience.complaints_data.importance}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Disclaimer */}
                    <p className="text-[#3A4557] text-xs italic border-t border-[#C9D4E6] pt-3">
                      {analysis.irdai_claim_experience.disclaimer}
                    </p>
                  </div>
                </ClickableItem>
              )}

              {/* FUTURE-PROOFING */}
              {analysis.future_proofing && analysis.future_proofing.rating && analysis.future_proofing.rating !== 'N/A' && (
                <ClickableItem
                  id="future_proofing"
                  title="Is This Policy Future-Proof?"
                  subtitle="Bonus quality, restoration, premium trajectory & long-term fit"
                  icon={TrendingUp}
                  color="from-cyan-600/30 to-blue-600/30"
                  badge={analysis.future_proofing.rating}
                  badgeColor={
                    analysis.future_proofing.rating === 'STRONG' ? 'bg-emerald-500/20 text-emerald-600' :
                    analysis.future_proofing.rating === 'WEAK' ? 'bg-rose-500/20 text-rose-600' :
                    'bg-amber-500/20 text-amber-600'
                  }
                >
                  <div className="space-y-3" data-testid="xray-future-proofing">
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { label: "No-Claim Bonus quality", value: analysis.future_proofing.ncb_assessment },
                        { label: "Restoration benefit", value: analysis.future_proofing.restoration_assessment },
                        { label: "Premium outlook with age", value: analysis.future_proofing.premium_outlook },
                        { label: "Lifetime renewal", value: analysis.future_proofing.lifetime_renewal }
                      ].filter(x => x.value).map((x, i) => (
                        <div key={i} className="p-3 rounded-lg bg-[#EDF1F8]/60 border border-[#C9D4E6]">
                          <p className="text-[#A87608] text-xs font-bold mb-1">{x.label}</p>
                          <p className="text-[#2E3B57] text-sm leading-relaxed">{x.value}</p>
                        </div>
                      ))}
                    </div>
                    {analysis.future_proofing.long_term_verdict && (
                      <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                        <p className="text-[#2E3B57] text-sm leading-relaxed"><span className="text-cyan-600 font-bold">10-year view: </span>{analysis.future_proofing.long_term_verdict}</p>
                      </div>
                    )}
                  </div>
                </ClickableItem>
              )}

              {/* 3. COMPANY VERDICT */}
              {analysis.company_verdict && analysis.company_verdict.summary && (
                <ClickableItem
                  id="company_verdict"
                  title="Company Verdict"
                  subtitle={analysis.company_verdict.summary?.substring(0, 80) + '...'}
                  icon={Award}
                  color="from-blue-600/30 to-indigo-600/30"
                  badge={analysis.company_verdict.rating}
                  badgeColor={
                    analysis.company_verdict.rating === 'EXCELLENT' ? 'bg-emerald-500 text-white' :
                    analysis.company_verdict.rating === 'GOOD' ? 'bg-blue-500 text-white' :
                    analysis.company_verdict.rating === 'CONCERNING' ? 'bg-red-500 text-white' :
                    'bg-[#D4A017] text-[#1B2A4A]'
                  }
                >
                  <div className="space-y-3">
                    <p className="text-[#2E3B57] text-sm leading-relaxed">{analysis.company_verdict.summary}</p>
                    {analysis.company_verdict.claim_handling && (
                      <div className="p-3 bg-[#EDF1F8]/30 border border-[#C9D4E6] rounded-lg">
                        <p className="text-[#2E3B57] text-xs font-semibold mb-1">Claim Handling</p>
                        <p className="text-[#2E3B57] text-sm">{analysis.company_verdict.claim_handling}</p>
                      </div>
                    )}
                  </div>
                </ClickableItem>
              )}

              {/* 4. PRODUCT VERDICT */}
              {analysis.product_verdict && (analysis.product_verdict.strengths?.length > 0 || analysis.product_verdict.weaknesses?.length > 0) && (
                <ClickableItem
                  id="product_verdict"
                  title="Product Verdict"
                  subtitle="How good is this specific product?"
                  icon={Shield}
                  color="from-purple-600/30 to-violet-600/30"
                  badge={analysis.product_verdict.rating}
                  badgeColor={
                    analysis.product_verdict.rating === 'EXCELLENT' ? 'bg-emerald-500 text-white' :
                    analysis.product_verdict.rating === 'GOOD' ? 'bg-blue-500 text-white' :
                    analysis.product_verdict.rating === 'CONCERNING' ? 'bg-red-500 text-white' :
                    'bg-[#D4A017] text-[#1B2A4A]'
                  }
                >
                  <div className="space-y-3">
                    {analysis.product_verdict.strengths?.length > 0 && (
                      <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                        <p className="text-emerald-600 text-xs font-semibold mb-2">Product Strengths</p>
                        <ul className="space-y-1">
                          {analysis.product_verdict.strengths.map((s, i) => (
                            <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                              <span className="text-emerald-600 mt-1">+</span> {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {analysis.product_verdict.weaknesses?.length > 0 && (
                      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-red-600 text-xs font-semibold mb-2">Product Weaknesses</p>
                        <ul className="space-y-1">
                          {analysis.product_verdict.weaknesses.map((w, i) => (
                            <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                              <span className="text-red-600 mt-1">-</span> {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {analysis.product_verdict.fine_print_warnings?.length > 0 && (
                      <div className="p-3 bg-[#D4A017]/10 border border-[#D4A017]/20 rounded-lg">
                        <p className="text-[#A87608] text-xs font-semibold mb-2">Fine Print Warnings</p>
                        <ul className="space-y-1">
                          {analysis.product_verdict.fine_print_warnings.map((w, i) => (
                            <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                              <span className="text-[#A87608] mt-1">!</span> {w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </ClickableItem>
              )}

              {/* 5. CLAIM READINESS SCORE */}
              {analysis.claim_readiness_score && analysis.claim_readiness_score.score > 0 && (
                <ClickableItem
                  id="claim_readiness"
                  title="Claim Readiness Score"
                  subtitle="If you need to claim tomorrow, how prepared are you?"
                  icon={Target}
                  color="from-rose-600/30 to-red-600/30"
                  badge={`${analysis.claim_readiness_score.score}/${analysis.claim_readiness_score.out_of || 10}`}
                  badgeColor={
                    analysis.claim_readiness_score.score >= 8 ? 'bg-emerald-500 text-white' :
                    analysis.claim_readiness_score.score >= 5 ? 'bg-[#D4A017] text-[#1B2A4A]' :
                    'bg-red-500 text-white'
                  }
                >
                  <div className="space-y-3">
                    <div className="text-center py-4">
                      <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 mb-3 ${
                        analysis.claim_readiness_score.score >= 8 ? 'border-emerald-500 text-emerald-600' :
                        analysis.claim_readiness_score.score >= 5 ? 'border-[#D4A017] text-[#A87608]' :
                        'border-red-500 text-red-600'
                      }`}>
                        <span className="text-2xl font-bold">{analysis.claim_readiness_score.score}</span>
                        <span className="text-sm opacity-60">/{analysis.claim_readiness_score.out_of || 10}</span>
                      </div>
                      <p className="text-[#1B2A4A] font-medium">{analysis.claim_readiness_score.verdict}</p>
                    </div>
                    {analysis.claim_readiness_score.biggest_risk_at_claim_time && (
                      <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-red-600 text-xs font-semibold mb-1">Biggest Risk at Claim Time</p>
                        <p className="text-[#2E3B57] text-sm">{analysis.claim_readiness_score.biggest_risk_at_claim_time}</p>
                      </div>
                    )}
                    {analysis.claim_readiness_score.what_to_fix_first && (
                      <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                        <p className="text-cyan-600 text-xs font-semibold mb-1">Fix This First</p>
                        <p className="text-[#2E3B57] text-sm">{analysis.claim_readiness_score.what_to_fix_first}</p>
                      </div>
                    )}
                  </div>
                </ClickableItem>
              )}

              {/* 3. KEY STRENGTHS & GAPS - Third */}
              {analysis.overall_score && (analysis.overall_score.key_strengths?.length > 0 || analysis.overall_score.key_gaps?.length > 0) && (
                <ClickableItem
                  id="key_strengths_gaps"
                  title="Key Strengths & Gaps"
                  subtitle="Quick overview of your policy's pros and cons"
                  icon={Target}
                  color="from-emerald-600/30 to-teal-600/30"
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                      <p className="text-emerald-600 font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        Key Strengths
                      </p>
                      <ul className="space-y-2">
                        {(analysis.overall_score.key_strengths || []).map((s, i) => (
                          <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                            <span className="text-emerald-600 font-bold">+</span>
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                      <p className="text-red-600 font-semibold mb-3 flex items-center gap-2">
                        <XCircle className="h-5 w-5" />
                        Key Gaps
                      </p>
                      <ul className="space-y-2">
                        {(analysis.overall_score.key_gaps || []).map((g, i) => (
                          <li key={i} className="text-[#2E3B57] text-sm flex items-start gap-2">
                            <span className="text-red-600 font-bold">-</span>
                            {g}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </ClickableItem>
              )}

              {/* 4. OVERALL SCORE - Fourth */}
              {analysis.overall_score && (
                <ClickableItem
                  id="overall_score"
                  title="Overall Policy Score"
                  subtitle={analysis.overall_score.one_line_verdict || analysis.overall_score.verdict}
                  icon={Award}
                  color="from-purple-600/30 to-pink-600/30"
                  badge={analysis.overall_score.score ? `${analysis.overall_score.score}/${analysis.overall_score.out_of || 10}` : analysis.overall_score.score}
                  badgeColor="bg-purple-500 text-white"
                >
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-4">
                      <span className="text-3xl font-bold text-[#1B2A4A]">{analysis.overall_score.score}/{analysis.overall_score.out_of || 10}</span>
                    </div>
                    <p className="text-[#2E3B57] text-sm max-w-md mx-auto">{analysis.overall_score.one_line_verdict || analysis.overall_score.verdict}</p>
                  </div>
                </ClickableItem>
              )}

              {/* What's Concerning */}
              {analysis.whats_concerning && analysis.whats_concerning.length > 0 && (
                <ClickableItem
                  id="whats_concerning"
                  title="What's Concerning"
                  subtitle="Issues that need your attention"
                  icon={AlertTriangle}
                  color="from-red-600/30 to-[#D9A82C]/30"
                  badge={analysis.whats_concerning.length}
                  badgeColor="bg-red-500 text-white"
                >
                  {analysis.whats_concerning.map((item, idx) => (
                    <SubItem key={idx} id={`concern_${idx}`} title={item.concern} status="Action Needed" statusColor="bg-red-500/30 text-red-700">
                      <p className="mb-2"><strong className="text-[#A87608]">Impact:</strong> {item.real_world_impact || item.impact}</p>
                      <p><strong className="text-emerald-600">What to do:</strong> {item.what_to_do || item.solution}</p>
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* What's Good */}
              {analysis.whats_good && analysis.whats_good.length > 0 && (
                <ClickableItem
                  id="whats_good"
                  title="What's Good"
                  subtitle="Strong points in your policy"
                  icon={CheckCircle2}
                  color="from-emerald-600/30 to-teal-600/30"
                  badge={analysis.whats_good.length}
                  badgeColor="bg-emerald-500 text-white"
                >
                  {analysis.whats_good.map((item, idx) => (
                    <SubItem key={idx} id={`good_${idx}`} title={item.point || item.feature} status="Good" statusColor="bg-emerald-500/30 text-emerald-700">
                      <p>{item.why_it_matters || item.why_good}</p>
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Waiting Period Analysis */}
              {analysis.waiting_period_analysis && analysis.waiting_period_analysis.length > 0 && (
                <ClickableItem
                  id="waiting_periods"
                  title="Waiting Period Status"
                  subtitle="Based on your policy inception date"
                  icon={Clock}
                  color="from-[#D4A017]/30 to-yellow-600/30"
                >
                  {analysis.waiting_period_analysis.map((wp, idx) => (
                    <SubItem 
                      key={idx} 
                      id={`wp_${idx}`} 
                      title={wp.type}
                      status={wp.status}
                      statusColor={wp.status === 'COMPLETED' ? 'bg-emerald-500/30 text-emerald-700' : 'bg-[#D4A017]/30 text-[#A87608]'}
                    >
                      <p className="mb-2"><strong>Standard Period:</strong> {wp.standard_period}</p>
                      {wp.status !== 'COMPLETED' && wp.remaining_days && (
                        <p className="text-[#A87608] mb-2">⏳ {wp.remaining_days} days remaining</p>
                      )}
                      {wp.applicable_conditions && wp.applicable_conditions.length > 0 && (
                        <p className="mb-2"><strong>Applies to:</strong> {Array.isArray(wp.applicable_conditions) ? wp.applicable_conditions.join(', ') : wp.applicable_conditions}</p>
                      )}
                      {wp.rider_that_can_reduce && (
                        <p className="text-cyan-600">💡 Rider that can reduce: {wp.rider_that_can_reduce}</p>
                      )}
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Rider Combination Verdict (prominent, always visible) */}
              {analysis.rider_combination_verdict && analysis.rider_combination_verdict.verdict && (() => {
                const rc = analysis.rider_combination_verdict;
                const styles = {
                  WELL_BUILT: { border: "border-emerald-500/40", bg: "bg-emerald-500/10", iconBg: "bg-emerald-500/20", iconColor: "text-emerald-600", text: "text-emerald-700", badge: "bg-emerald-500/25 text-emerald-800", label: "Well Built" },
                  DECENT: { border: "border-cyan-500/40", bg: "bg-cyan-500/10", iconBg: "bg-cyan-500/20", iconColor: "text-cyan-600", text: "text-cyan-700", badge: "bg-cyan-500/25 text-cyan-800", label: "Decent" },
                  HAS_GAPS: { border: "border-amber-500/40", bg: "bg-amber-500/10", iconBg: "bg-amber-500/20", iconColor: "text-amber-600", text: "text-amber-700", badge: "bg-amber-500/25 text-amber-800", label: "Has Gaps" },
                  OVER_INSURED: { border: "border-rose-500/40", bg: "bg-rose-500/10", iconBg: "bg-rose-500/20", iconColor: "text-rose-600", text: "text-rose-700", badge: "bg-rose-500/25 text-rose-800", label: "Over-Insured" },
                };
                const s = styles[rc.verdict] || { border: "border-[#C9D4E6]", bg: "bg-[#EDF1F8]/30", iconBg: "bg-[#EDF1F8]/60", iconColor: "text-[#2E3B57]", text: "text-[#1B2A4A]", badge: "bg-[#EDF1F8] text-[#2E3B57]", label: rc.verdict };
                return (
                  <div data-testid="rider-combination-verdict" className={`rounded-2xl border p-5 mb-3 ${s.border} ${s.bg}`}>
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.iconBg}`}>
                        <Award className={`h-5 w-5 ${s.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <p className="text-[#3A4557] text-xs font-semibold uppercase tracking-wide">Your Rider Combination</p>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${s.badge}`}>{s.label}</span>
                        </div>
                        {rc.one_liner && <p className={`font-bold text-base sm:text-lg leading-snug ${s.text} mb-1.5`}>{rc.one_liner}</p>}
                        {rc.summary && <p className="text-[#2E3B57] text-sm leading-relaxed mb-3">{rc.summary}</p>}
                        {rc.current_stack && rc.current_stack.length > 0 && (
                          <div className="mb-3">
                            <p className="text-[#3A4557] text-xs mb-1.5">What you currently have</p>
                            <div className="flex flex-wrap gap-1.5">
                              {rc.current_stack.map((item, i) => (
                                <span key={i} className="px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-800 text-xs">{item}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {rc.gaps && rc.gaps.length > 0 && (
                          <div>
                            <p className="text-[#3A4557] text-xs mb-1.5">Gaps to consider</p>
                            <ul className="space-y-1">
                              {rc.gaps.map((g, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-[#2E3B57]">
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <span>{g}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Riders Analysis */}
              {analysis.riders_analysis && (
                <ClickableItem
                  id="riders"
                  title="Riders & Add-ons Analysis"
                  subtitle="What you have vs what's available"
                  icon={Zap}
                  color="from-cyan-600/30 to-[#DCE4F2]/30"
                >
                  {/* Riders Taken */}
                  {analysis.riders_analysis.riders_taken_by_customer && analysis.riders_analysis.riders_taken_by_customer.length > 0 && (
                    <div className="mb-4">
                      <p className="text-cyan-600 font-semibold mb-2">Riders You Have</p>
                      {analysis.riders_analysis.riders_taken_by_customer.map((r, idx) => (
                        <SubItem 
                          key={idx} 
                          id={`rider_taken_${idx}`} 
                          title={r.rider_name}
                          status={(r.is_useful || r.is_useful_for_this_customer) ? "Useful" : "May Not Need"}
                          statusColor={(r.is_useful || r.is_useful_for_this_customer) ? 'bg-emerald-500/30 text-emerald-700' : 'bg-[#D4A017]/30 text-[#A87608]'}
                        >
                          <p className="mb-2"><strong>Benefit:</strong> {r.benefit}</p>
                          <p><strong>Assessment:</strong> {r.honest_assessment || r.reason}</p>
                        </SubItem>
                      ))}
                    </div>
                  )}

                  {/* Unnecessary Riders Flag */}
                  {analysis.riders_analysis.unnecessary_riders_flag && analysis.riders_analysis.unnecessary_riders_flag.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[#A87608] font-semibold mb-2">Unnecessary Riders (Wasting Money)</p>
                      {analysis.riders_analysis.unnecessary_riders_flag.map((r, idx) => (
                        <SubItem key={idx} id={`rider_waste_${idx}`} title={r.rider_name} status="Unnecessary" statusColor="bg-[#D4A017]/30 text-[#A87608]">
                          <p className="mb-2"><strong>Why unnecessary:</strong> {r.why_unnecessary}</p>
                          {r.money_wasted_approx && <p className="text-red-600"><strong>Approx wasted:</strong> {r.money_wasted_approx}</p>}
                        </SubItem>
                      ))}
                    </div>
                  )}

                  {/* Missing Riders */}
                  {analysis.riders_analysis.riders_missing_but_recommended && analysis.riders_analysis.riders_missing_but_recommended.length > 0 && (
                    <div className="mb-4">
                      <p className="text-red-600 font-semibold mb-2">✗ Recommended Riders You Don't Have</p>
                      {analysis.riders_analysis.riders_missing_but_recommended.map((r, idx) => (
                        <SubItem key={idx} id={`rider_missing_${idx}`} title={r.rider_name} status="Consider Adding" statusColor="bg-red-500/30 text-red-700">
                          <p className="mb-2"><strong>Why recommended:</strong> {r.why_recommended}</p>
                          <p className="text-red-600"><strong>Risk without it:</strong> {r.risk_without_it}</p>
                        </SubItem>
                      ))}
                    </div>
                  )}

                  {/* Available Riders */}
                  {analysis.riders_analysis.riders_available_for_product && analysis.riders_analysis.riders_available_for_product.length > 0 && (
                    <div>
                      <p className="text-[#3A4557] font-semibold mb-2">All Available Riders for This Product</p>
                      {analysis.riders_analysis.riders_available_for_product.map((r, idx) => (
                        <SubItem key={idx} id={`rider_avail_${idx}`} title={r.rider_name}>
                          <p className="mb-1"><strong>Purpose:</strong> {r.purpose}</p>
                          <p className="mb-1"><strong>Recommended for:</strong> {r.recommended_for}</p>
                          {r.premium_impact && <p className="text-[#3A4557]">Premium impact: {r.premium_impact}</p>}
                        </SubItem>
                      ))}
                    </div>
                  )}
                </ClickableItem>
              )}

              {/* PED Analysis */}
              {analysis.ped_analysis && (
                <ClickableItem
                  id="ped_analysis"
                  title="Pre-Existing Disease (PED) Analysis"
                  subtitle="Coverage status for your declared conditions"
                  icon={Heart}
                  color="from-pink-600/30 to-rose-600/30"
                >
                  <div className="space-y-3">
                    <div className="p-3 bg-[#EDF1F8]/60 rounded-lg">
                      <p className="text-[#3A4557] text-sm">Declared Conditions</p>
                      <p className="text-[#1B2A4A]">{Array.isArray(analysis.ped_analysis.declared_conditions) ? (analysis.ped_analysis.declared_conditions.length > 0 ? analysis.ped_analysis.declared_conditions.join(', ') : 'None declared') : (analysis.ped_analysis.declared_conditions || 'None declared')}</p>
                    </div>
                    <div className="p-3 bg-[#EDF1F8]/60 rounded-lg">
                      <p className="text-[#3A4557] text-sm">Waiting Period Status</p>
                      <p className={analysis.ped_analysis.waiting_period_status?.includes('COMPLETED') ? 'text-emerald-600' : 'text-[#A87608]'}>
                        {analysis.ped_analysis.waiting_period_status || 'Unknown'}
                      </p>
                    </div>
                    {analysis.ped_analysis.recommendation && (
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <p className="text-purple-600 font-medium">💡 Recommendation</p>
                        <p className="text-[#2E3B57] text-sm">{analysis.ped_analysis.recommendation}</p>
                      </div>
                    )}
                  </div>
                </ClickableItem>
              )}

              {/* Sub-limits */}
              {analysis.sub_limits_and_caps && analysis.sub_limits_and_caps.length > 0 && (
                <ClickableItem
                  id="sub_limits"
                  title="Sub-limits & Caps"
                  subtitle="Limits that may affect your claims"
                  icon={TrendingDown}
                  color="from-orange-600/30 to-red-600/30"
                  badge={analysis.sub_limits_and_caps.length}
                >
                  {analysis.sub_limits_and_caps.map((item, idx) => (
                    <SubItem key={idx} id={`sublimit_${idx}`} title={item.item} status={item.red_flag ? "RED FLAG" : item.your_limit} statusColor={item.red_flag ? 'bg-red-500/30 text-red-700' : undefined}>
                      <p className="mb-1"><strong>Your limit:</strong> {item.your_limit}</p>
                      {item.market_standard && <p className="mb-1"><strong>Market standard:</strong> {item.market_standard}</p>}
                      <p className="mb-2"><strong>At claim time:</strong> {item.impact_at_claim || item.impact}</p>
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Coverage Analysis */}
              {analysis.coverage_analysis && analysis.coverage_analysis.length > 0 && (
                <ClickableItem
                  id="coverage"
                  title="Coverage Analysis"
                  subtitle="Feature-by-feature breakdown"
                  icon={Shield}
                  color="from-indigo-600/30 to-purple-600/30"
                  badge={analysis.coverage_analysis.length}
                >
                  {analysis.coverage_analysis.map((item, idx) => (
                    <SubItem 
                      key={idx} 
                      id={`coverage_${idx}`} 
                      title={item.feature}
                      status={item.assessment}
                      statusColor={
                        ['GOOD', 'STRONG'].includes(item.assessment) ? 'bg-emerald-500/30 text-emerald-700' :
                        ['NEEDS_ATTENTION', 'WEAK', 'RED_FLAG'].includes(item.assessment) ? 'bg-red-500/30 text-red-700' :
                        'bg-[#D4A017]/30 text-[#A87608]'
                      }
                    >
                      <p className="mb-1"><strong>Your coverage:</strong> {item.what_you_get || item.what_policy_provides}</p>
                      {item.industry_standard && <p className="mb-1"><strong>Industry standard:</strong> {item.industry_standard}</p>}
                      <p><strong>In simple words:</strong> {item.plain_english || item.explanation}</p>
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Exclusions */}
              {analysis.exclusions_to_know && analysis.exclusions_to_know.length > 0 && (
                <ClickableItem
                  id="exclusions"
                  title="Key Exclusions"
                  subtitle="What's NOT covered"
                  icon={XCircle}
                  color="from-red-600/30 to-pink-600/30"
                  badge={analysis.exclusions_to_know.length}
                >
                  {analysis.exclusions_to_know.map((item, idx) => (
                    <SubItem 
                      key={idx} 
                      id={`exclusion_${idx}`} 
                      title={item.exclusion}
                      status={item.type}
                      statusColor={item.type === 'Permanent' ? 'bg-red-500/30 text-red-700' : 'bg-[#D4A017]/30 text-[#A87608]'}
                    >
                      {(item.plain_english || item.workaround) && (
                        <p className="text-cyan-600">{item.plain_english || item.workaround}</p>
                      )}
                      {item.workaround_if_any && (
                        <p className="text-emerald-600 mt-1">Workaround: {item.workaround_if_any}</p>
                      )}
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Personalized Advice */}
              {analysis.personalized_advice && analysis.personalized_advice.length > 0 && (
                <ClickableItem
                  id="personalized_advice"
                  title="Personalized Advice"
                  subtitle="Tailored recommendations based on YOUR situation"
                  icon={MessageSquareWarning}
                  color="from-cyan-600/30 to-teal-600/30"
                  badge={analysis.personalized_advice.length}
                  badgeColor="bg-cyan-500 text-white"
                >
                  {analysis.personalized_advice.map((item, idx) => (
                    <SubItem 
                      key={idx} 
                      id={`advice_${idx}`} 
                      title={item.advice}
                      status={item.priority}
                      statusColor={
                        item.priority === 'HIGH' ? 'bg-red-500/30 text-red-700' :
                        item.priority === 'MEDIUM' ? 'bg-[#D4A017]/30 text-[#A87608]' :
                        'bg-[#EDF1F8]/40 text-[#2E3B57]'
                      }
                    >
                      <p className="text-cyan-600 text-sm italic">{item.applicable_if}</p>
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Action Items */}
              {analysis.action_items && analysis.action_items.length > 0 && (
                <ClickableItem
                  id="actions"
                  title="Recommended Actions"
                  subtitle="What you should do next"
                  icon={Target}
                  color="from-green-600/30 to-emerald-600/30"
                  badge={analysis.action_items.length}
                >
                  {analysis.action_items.map((item, idx) => (
                    <SubItem 
                      key={idx} 
                      id={`action_${idx}`} 
                      title={item.action}
                      status={item.priority}
                      statusColor={
                        item.priority === 'URGENT' ? 'bg-red-600/30 text-red-800' :
                        item.priority === 'HIGH' ? 'bg-red-500/30 text-red-700' :
                        item.priority === 'MEDIUM' ? 'bg-[#D4A017]/30 text-[#A87608]' :
                        'bg-[#EDF1F8]/40 text-[#2E3B57]'
                      }
                    >
                      <p className="mb-1"><strong>Reason:</strong> {item.reason}</p>
                      <p><strong>When:</strong> {item.when_to_do || item.when}</p>
                    </SubItem>
                  ))}
                </ClickableItem>
              )}

              {/* Beta Disclaimer - Bottom */}
              <div className="mt-8 p-5 rounded-xl bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/10 border border-[#D4A017]/30">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-[#D4A017]/15 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-[#A87608]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#A87608] font-bold">Beta Feature - Use With Caution</span>
                      <span className="px-2 py-0.5 bg-[#D4A017]/15 text-[#A87608] text-xs rounded-full">Experimental</span>
                    </div>
                    <p className="text-[#8F6A06]/80 text-sm leading-relaxed mb-3">
                      This AI-powered analysis is in <strong>beta</strong> and may contain inaccuracies. The results are generated using AI interpretation and should be treated as <strong>indicative guidance only</strong>, not as definitive policy advice.
                    </p>
                    <div className="bg-[#EDF1F8]/60 rounded-lg p-3 space-y-2">
                      <p className="text-[#3A4557] text-xs flex items-start gap-2">
                        <span className="text-[#A87608]">•</span>
                        Always verify coverage details, exclusions, and claim procedures directly with your insurance company.
                      </p>
                      <p className="text-[#3A4557] text-xs flex items-start gap-2">
                        <span className="text-[#A87608]">•</span>
                        IRDAI data shown is from official sources but may have a lag in reporting periods.
                      </p>
                      <p className="text-[#3A4557] text-xs flex items-start gap-2">
                        <span className="text-[#A87608]">•</span>
                        Consult a qualified insurance advisor before making any policy decisions based on this report.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PolicyXRayPage;
